import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Markup, MarkUpType } from '../model/markup.model';

type MarkupRulesMode = 'global' | 'client';
type MarkupRulesLayout = 'table' | 'sidebar';

@Component({
  selector: 'app-markup-rules-table',
  templateUrl: './markup-rules-table.component.html',
  styleUrls: ['./markup-rules-table.component.scss']
})
export class MarkupRulesTableComponent implements OnChanges {
  @Input() rules: Markup[] = [];
  @Input() loading = false;
  @Input() readonly = false;
  @Input() mode: MarkupRulesMode = 'global';
  @Input() allowRecalculate = false;
  @Input() recalculating = false;
  @Input() sortable = false;
  @Input() layout: MarkupRulesLayout = 'table';

  @Output() createRule = new EventEmitter<void>();
  @Output() editRule = new EventEmitter<Markup>();
  @Output() deleteRule = new EventEmitter<Markup>();
  @Output() refreshRules = new EventEmitter<void>();
  @Output() recalculate = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<Sort>();

  displayedColumns = ['range', 'rule', 'round', 'conditions', 'priority', 'actions'];

  private warningsByRuleId = new Map<number, string[]>();
  private moneyFormatter = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  private numberFormatter = new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 2
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rules || changes.mode) {
      this.rebuildWarnings();
    }
  }

  get title() {
    return this.mode === 'global' ? 'Глобальная наценка' : 'Наценка клиента';
  }

  get subtitle() {
    return this.mode === 'global'
      ? 'Используется, если нет клиентского правила'
      : 'Имеет приоритет над глобальной таблицей';
  }

  get badgeLabel() {
    if (this.mode === 'client') return 'Можно менять менеджеру';
    return this.readonly ? 'Только просмотр' : 'Только admin может изменять';
  }

  get emptyMessage() {
    return this.mode === 'client'
      ? 'Для клиента используются глобальные правила'
      : 'Правила наценки пока не добавлены';
  }

  get hasRules() {
    return (this.rules || []).length > 0;
  }

  get isSidebarLayout() {
    return this.layout === 'sidebar';
  }

  confirmDelete(rule: Markup) {
    const confirmed = globalThis.confirm(`Удалить правило наценки #${rule.id}?`);
    if (confirmed) {
      this.deleteRule.emit(rule);
    }
  }

  confirmRecalculate() {
    const confirmed = globalThis.confirm(
      'Клиентские правила не изменятся, будет пересчитан product.retail по глобальной таблице'
    );
    if (confirmed) {
      this.recalculate.emit();
    }
  }

  formatRange(rule: Markup) {
    const start = this.formatMoney(rule.rangeStart);
    if (rule.rangeEnd === null || rule.rangeEnd === undefined) {
      return `${start}+`;
    }
    return `${start} - ${this.formatMoney(rule.rangeEnd)}`;
  }

  formatMarkup(rule: Markup) {
    if (rule.type === MarkUpType.FIXED) {
      return `+${this.formatMoney(rule.markup)}`;
    }
    return `+${this.formatNumber(rule.markup)}%`;
  }

  formatRound(rule: Markup) {
    return `до ${this.formatNumber(rule.round)} грн`;
  }

  getConditionChips(rule: Markup) {
    const chips: string[] = [];
    if (rule.folder?.description) {
      chips.push(`Папка: ${rule.folder.description}`);
    }

    const brands = rule.brands || [];
    if (brands.length) {
      const visibleBrands = brands.slice(0, 2).map((brand) => brand.description).join(', ');
      const hiddenCount = brands.length - 2;
      chips.push(hiddenCount > 0 ? `Бренды: ${visibleBrands} +${hiddenCount}` : `Бренды: ${visibleBrands}`);
    }

    return chips.length ? chips : ['Все товары'];
  }

  getRuleWarnings(rule: Markup) {
    return this.warningsByRuleId.get(rule.id) || [];
  }

  getTemplateLabel(rule: Markup) {
    if (!rule.templateGenerated) return '';
    if (rule.templateDelta === null || rule.templateDelta === undefined) {
      return 'История 12м';
    }
    const delta = Number(rule.templateDelta || 0);
    const sign = delta > 0 ? '+' : '';
    return `Шаблон ${sign}${this.formatNumber(delta)}%`;
  }

  trackByRuleId(_index: number, rule: Markup) {
    return rule.id;
  }

  private rebuildWarnings() {
    this.warningsByRuleId = new Map<number, string[]>();
    const rules = this.rules || [];

    rules.forEach((rule) => {
      const warnings: string[] = [];
      if (this.hasInvalidRange(rule)) {
        warnings.push('Конец диапазона меньше начала');
      }
      if (Number(rule.round) <= 0) {
        warnings.push('Округление должно быть больше 0');
      }
      if (warnings.length) {
        this.addWarnings(rule, warnings);
      }
    });

    for (let leftIndex = 0; leftIndex < rules.length; leftIndex++) {
      for (let rightIndex = leftIndex + 1; rightIndex < rules.length; rightIndex++) {
        const left = rules[leftIndex];
        const right = rules[rightIndex];
        if (this.conditionsKey(left) !== this.conditionsKey(right)) continue;
        if (!this.rangesOverlap(left, right)) continue;
        this.addWarnings(left, [`Пересечение с правилом #${right.id}`]);
        this.addWarnings(right, [`Пересечение с правилом #${left.id}`]);
      }
    }

    if (this.mode === 'global') {
      this.addBaseRangeGapWarnings(rules);
    }
  }

  private addBaseRangeGapWarnings(rules: Markup[]) {
    const baseRules = rules
      .filter((rule) => !rule.folder && !(rule.brands || []).length)
      .sort((left, right) => Number(left.rangeStart) - Number(right.rangeStart));

    for (let index = 1; index < baseRules.length; index++) {
      const previous = baseRules[index - 1];
      const current = baseRules[index];
      const previousEnd = this.rangeEnd(previous);
      if (!Number.isFinite(previousEnd)) continue;
      if (Number(current.rangeStart) - previousEnd > 0.011) {
        this.addWarnings(current, [`Разрыв после ${this.formatMoney(previousEnd)}`]);
      }
    }
  }

  private addWarnings(rule: Markup, warnings: string[]) {
    const current = this.warningsByRuleId.get(rule.id) || [];
    this.warningsByRuleId.set(rule.id, [...current, ...warnings]);
  }

  private hasInvalidRange(rule: Markup) {
    return rule.rangeEnd !== null && rule.rangeEnd !== undefined && Number(rule.rangeEnd) < Number(rule.rangeStart);
  }

  private rangesOverlap(left: Markup, right: Markup) {
    return Number(left.rangeStart) <= this.rangeEnd(right) && Number(right.rangeStart) <= this.rangeEnd(left);
  }

  private rangeEnd(rule: Markup) {
    return rule.rangeEnd === null || rule.rangeEnd === undefined ? Infinity : Number(rule.rangeEnd);
  }

  private conditionsKey(rule: Markup) {
    const folderKey = rule.folder?.id ? `folder:${rule.folder.id}` : 'folder:any';
    const brandKey = (rule.brands || [])
      .map((brand) => Number(brand.id))
      .sort((left, right) => left - right)
      .join(',');
    return `${folderKey}|brands:${brandKey || 'any'}`;
  }

  private formatMoney(value: number) {
    return `${this.moneyFormatter.format(Number(value || 0))} грн`;
  }

  private formatNumber(value: number) {
    return this.numberFormatter.format(Number(value || 0));
  }
}
